/**
 * Per-page RIGHT-rail filters (founder 2026-06-28: "two sidebars — left = pages,
 * right = product toggles etc"). Holds the active PRODUCT selection for the CURRENT
 * page, persisted per page, and injects it into every API request via the
 * "pageFilters" global-query namespace — so all the page's cards re-slice at once, the
 * same way the gross/net basis does (no per-card wiring).
 *
 * PRODUCT-ONLY since the blended model (founder 2026-07-13): the old first-touch
 * acquisition groups (Source / Affiliate / Discount) were removed from the rail —
 * channel is a DRILL dimension now. The AcqSel type + acq/setAcq context stay as inert
 * machinery (the drill sets its own per-order filters), but the rail no longer injects
 * acquisition params, and a saved acq selection is dropped on load (loadState).
 *
 * Empty selection = no filter (everything). A card reads usePageFilters() only to KNOW
 * the product filter is active — e.g. a blended spend card (MER/CAC/overhead) shows a
 * "Blended" badge because spend isn't per-product, so it intentionally stays unfiltered
 * (founder's "never ratio mismatched populations").
 */

import { createContext, useContext, useMemo, useRef, useState } from "react";
import { useViewportWidth, BREAK } from "./use-viewport.js";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { HierProductFilter, RailGroup, CheckRow } from "../components/DrillExplorer.js";
import { buildProductHierarchy, useProductSkusQuery, useResizableWidth } from "../components/ListControls.js";
import { useDisplayOpts } from "./display-opts.js";
import { WholesaleToggle } from "./wholesale-toggle.js";
import { setGlobalQuery } from "./api-client.js";
import { invalidateFilterQueries } from "./query-invalidation.js";
import { pageLoadState } from "./page-load-state.js";
import { accountScopedStorageKey } from "./account-browser-state.js";

/** The page rail's FIRST-TOUCH acquisition selections (how the customer was acquired). */
export interface AcqSel { sources: string[]; affiliates: string[]; discounts: string[] }
const EMPTY_ACQ: AcqSel = { sources: [], affiliates: [], discounts: [] };
const acqCount = (a: AcqSel): number => a.sources.length + a.affiliates.length + a.discounts.length;

interface PageFiltersCtx {
  pageKey: string;
  products: string[]; // leaf names (group / product / sku_code); empty = all
  setProducts: (v: string[]) => void;
  acq: AcqSel;
  setAcq: (v: AcqSel) => void;
  productActive: boolean; // true when narrowed to a product subset
  /** Product filter active — blended cards show their "Blended" badge on this, since
   *  spend/overhead aren't tracked per product. Kept named `filterActive` for the many
   *  card call-sites; equals productActive now the rail is product-only. */
  filterActive: boolean;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}
const Ctx = createContext<PageFiltersCtx | null>(null);

const NO_FILTERS: PageFiltersCtx = {
  pageKey: "", products: [], setProducts: () => {}, acq: EMPTY_ACQ, setAcq: () => {},
  productActive: false, filterActive: false, collapsed: false, setCollapsed: () => {},
};

export function pageFilterStorageKey(accountId: string, pageKey: string): string {
  return accountScopedStorageKey(accountId, `page-filters:${pageKey}`);
}

export function pageFilterCollapsedStorageKey(accountId: string, pageKey: string): string {
  return accountScopedStorageKey(accountId, `page-filters-collapsed:${pageKey}`);
}

export function loadPageFilterState(accountId: string, pageKey: string, filtersEnabled = true): { products: string[]; acq: AcqSel } {
  // A hidden rail must also mean a disabled query filter. In particular, old
  // subscription product selections cannot be trusted until plan records are
  // reliably linked to imported orders.
  if (!filtersEnabled) return { products: [], acq: EMPTY_ACQ };
  try {
    const raw = localStorage.getItem(pageFilterStorageKey(accountId, pageKey));
    const p = raw ? (JSON.parse(raw) as { products?: string[]; acq?: Partial<AcqSel> }) : null;
    // MIGRATION (blended model 2026-07-13): the top-level rail is PRODUCT-ONLY now.
    // Any acquisition selection saved by the old Source / Affiliate / Discount groups
    // is DROPPED on load — otherwise a user with a saved affiliate filter would get
    // invisibly-narrowed pages with no visible control to clear it. Products are kept.
    return {
      products: Array.isArray(p?.products) ? p!.products! : [],
      acq: EMPTY_ACQ,
    };
  } catch {
    return { products: [], acq: EMPTY_ACQ };
  }
}

/** Translate the saved PRODUCT selection into the global query param every card
 *  request carries. The rail is product-only now — acquisition (acqSource/Affiliate/
 *  Discount) is NO LONGER injected here (the AcqFilter type stays for the drill,
 *  which sets its own per-order filters). */
export function applyPageFilterQuery(products: string[], filtersEnabled = true): void {
  setGlobalQuery({
    product: filtersEnabled && products.length ? products : undefined,
  }, "pageFilters");
}

/** Product selections are sets even though the rail stores them as arrays. */
export function sameProductSelection(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const selected = new Set(a);
  return b.every((product) => selected.has(product));
}

/** Which routes get the filter sidebar (pageKey = first path segment, e.g.
 *  "marketing" or "demo-marketing"). Every DATA page gets it — same sidebar
 *  everywhere (founder 2026-06-28). Non-data routes (connections/settings/etc.)
 *  don't. */
export function pageHasProductFilter(pageKey: string, subscriptionOrderLinkage?: boolean): boolean {
  const seg = pageKey.replace(/^demo-/, "");
  if (seg === "subscriptions") return subscriptionOrderLinkage === true;
  return ["dashboard", "marketing", "products", "cohorts", "retention", "subscriptions", "operations", "home"].includes(seg);
}

export function PageFiltersProvider({ accountId, pageKey, filtersEnabled = true, children }: { accountId: string; pageKey: string; filtersEnabled?: boolean; children: React.ReactNode }): React.ReactElement {
  const qc = useQueryClient();
  const initial = loadPageFilterState(accountId, pageKey, filtersEnabled);
  const [products, setProductsState] = useState<string[]>(() => initial.products);
  const [acq, setAcqState] = useState<AcqSel>(() => initial.acq);
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.innerWidth < BREAK.rail) return true; // small screens start collapsed
    try { return localStorage.getItem(pageFilterCollapsedStorageKey(accountId, pageKey)) === "1"; } catch { return false; }
  });
  // Auto-collapse the rail on narrow windows (founder 2026-07-05), same
  // crossing pattern as the left nav: auto moves never touch the saved
  // preference, and re-expanding by hand on a small screen still works.
  const vw = useViewportWidth();
  const prevSmall = useRef(vw < BREAK.rail);
  useEffect(() => {
    const small = vw < BREAK.rail;
    if (small && !prevSmall.current) setCollapsedState(true);
    if (!small && prevSmall.current) {
      try { setCollapsedState(localStorage.getItem(pageFilterCollapsedStorageKey(accountId, pageKey)) === "1"); } catch { setCollapsedState(false); }
    }
    prevSmall.current = small;
  }, [accountId, vw, pageKey]);

  // Apply this page's saved filters to outgoing requests on mount; clear on leave so
  // they never bleed into the next page. The provider is keyed by pageKey in the
  // layout, so it remounts (and re-applies the right filter) on every navigation.
  useEffect(() => {
    applyPageFilterQuery(products, filtersEnabled);
    return () => setGlobalQuery({}, "pageFilters");
  }, [filtersEnabled, pageKey]);

  const persist = (p: string[], a: AcqSel): void => {
    try { localStorage.setItem(pageFilterStorageKey(accountId, pageKey), JSON.stringify({ products: p, acq: a })); } catch { /* ignore */ }
  };
  const setProducts = (v: string[]): void => {
    const next = filtersEnabled ? v : [];
    setProductsState(next);
    applyPageFilterQuery(next, filtersEnabled); // before the refetch
    persist(next, acq);
    void invalidateFilterQueries(qc);
  };
  // setAcq stays for the AcqSel machinery/types, but the product-only rail never
  // calls it and acquisition is no longer injected — the drill sets its own
  // per-order filters directly (channel/discount), independent of this state.
  const setAcq = (v: AcqSel): void => {
    setAcqState(v);
    persist(products, v);
    void invalidateFilterQueries(qc);
  };
  const setCollapsed = (v: boolean): void => {
    setCollapsedState(v);
    try { localStorage.setItem(pageFilterCollapsedStorageKey(accountId, pageKey), v ? "1" : "0"); } catch { /* ignore */ }
  };

  const value = useMemo<PageFiltersCtx>(
    () => ({
      pageKey, products, setProducts, acq, setAcq,
      productActive: filtersEnabled && products.length > 0,
      filterActive: filtersEnabled && (products.length > 0 || acqCount(acq) > 0),
      collapsed, setCollapsed,
    }),
    [pageKey, products, acq, collapsed, filtersEnabled],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Safe outside a provider (returns no-filters) so cards never crash. */
export function usePageFilters(): PageFiltersCtx {
  return useContext(Ctx) ?? NO_FILTERS;
}

/**
 * The right-hand filter sidebar shown on top-level pages — the PRODUCT/SKU filter
 * (plus the revenue-basis toggles). Product-only since the blended model shipped
 * (2026-07-13): channel/source is a DRILL dimension now, not a top-level rail
 * control. Collapsible to a thin strip.
 */
export function PageFilterRail(): React.ReactElement {
  const pf = usePageFilters();
  const productSkusQ = useProductSkusQuery();
  const hierarchy = useMemo(() => buildProductHierarchy(productSkusQ.data), [productSkusQ.data]);
  const productSkusLoad = pageLoadState(productSkusQ);
  const d = useDisplayOpts();
  const [grouping, setGrouping] = useState<"product" | "sku">("product");
  // Checkbox clicks stay local until Apply. This prevents each click in a
  // multi-product selection from starting a full page-wide request wave.
  const [draftProducts, setDraftProducts] = useState<string[]>(pf.products);
  useEffect(() => { setDraftProducts(pf.products); }, [pf.pageKey, pf.products]);
  const dirty = !sameProductSelection(draftProducts, pf.products);
  const count = draftProducts.length;
  const grossLtvPage = pf.pageKey.replace(/^demo-/, "") === "cohorts";
  // Drag-to-resize, same mechanism as the drill rail (own LS key so it's independent).
  const { width: railW, onGripDown } = useResizableWidth(264, "dd-page-rail-w", 230, 560);

  const clearAll = (): void => {
    setDraftProducts([]);
    if (pf.products.length > 0) pf.setProducts([]);
  };

  if (pf.collapsed) {
    return (
      <aside style={strip} onClick={() => pf.setCollapsed(false)} role="button" aria-label="Show filters" title="Show filters">
        <PanelRightOpen size={18} strokeWidth={1.8} style={{ color: "var(--body)" }} />
        <span style={stripLabel}>Filters{count > 0 ? ` (${count})` : ""}</span>
      </aside>
    );
  }
  return (
    <aside style={{ ...rail, width: railW }}>
      <RailGrip onGripDown={onGripDown} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={railTitle}>Filters{count > 0 ? ` (${count})` : ""}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {count > 0 && <button onClick={clearAll} style={clearBtn}>Clear</button>}
          <button onClick={() => pf.setCollapsed(true)} style={iconBtn} title="Hide filters" aria-label="Hide filters">
            <PanelRightClose size={16} strokeWidth={1.8} />
          </button>
        </div>
      </div>
      {grossLtvPage ? (
        <RailGroup label="Gross LTV basis">
          <div style={{ fontSize: 11.5, color: "var(--body)", lineHeight: 1.5 }}>
            Sales after recorded refunds, excluding sales tax and including shipping charged. This fixed basis keeps every customer group comparable.
          </div>
          <WholesaleToggle variant="row" />
        </RailGroup>
      ) : (
        <RailGroup label="Revenue adjustments">
          <CheckRow label="Subtract refunds" sub="show what you actually kept"
            on={d.refunds === "net"} onClick={() => d.set({ refunds: d.refunds === "net" ? "gross" : "net" })} />
          <CheckRow label="Exclude sales tax" sub="tax is pass-through, not revenue"
            on={d.tax === "exclude"} onClick={() => d.set({ tax: d.tax === "exclude" ? "include" : "exclude" })} />
          <CheckRow label="Include shipping" sub="count shipping charged as revenue"
            on={d.shipping === "include"} onClick={() => d.set({ shipping: d.shipping === "include" ? "exclude" : "include" })} />
          <WholesaleToggle variant="row" />
        </RailGroup>
      )}
      <HierProductFilter
        hierarchy={hierarchy}
        grouping={grouping}
        onGrouping={(g) => { setGrouping(g); clearAll(); }}
        selected={draftProducts}
        onChange={setDraftProducts}
        catalogLoading={productSkusQ.isLoading}
        catalogLoad={productSkusLoad}
      />
      <button
        type="button"
        disabled={!dirty}
        onClick={() => pf.setProducts(draftProducts)}
        style={{ ...applyBtn, opacity: dirty ? 1 : 0.48, cursor: dirty ? "pointer" : "default" }}
      >
        {dirty ? "Apply filters" : "Filters applied"}
      </button>
      <RailGroup label="Note">
        <div style={{ fontSize: 11, color: "var(--faint)", lineHeight: 1.45 }}>
          Cards marked Blended don’t change with these filters — spend and overhead aren’t tracked per product. To see a number by channel, open a card and switch “View by” to Channel.
        </div>
      </RailGroup>
    </aside>
  );
}

/** Drag handle on the rail's left edge — drag left to widen (same feel as the drill rail). */
function RailGrip({ onGripDown }: { onGripDown: (e: React.MouseEvent) => void }): React.ReactElement {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseDown={onGripDown} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title="Drag to resize" aria-hidden="true"
      style={{ position: "absolute", left: -6, top: 0, bottom: 0, width: 13, cursor: "col-resize", zIndex: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: hover ? 5 : 3, height: hover ? 48 : 36, borderRadius: 3, background: hover ? "var(--accent)" : "var(--border)", transition: "background .12s, width .12s, height .12s" }} />
    </div>
  );
}

const rail: React.CSSProperties = {
  width: 264, flexShrink: 0, alignSelf: "stretch", position: "sticky", top: 0, maxHeight: "100vh", overflowY: "auto",
  padding: "18px 16px 24px", borderLeft: "1px solid var(--border)", background: "var(--surface)",
  display: "flex", flexDirection: "column", gap: 14,
};
const strip: React.CSSProperties = {
  width: 46, flexShrink: 0, alignSelf: "stretch", position: "sticky", top: 0, maxHeight: "100vh",
  borderLeft: "1px solid var(--border)", background: "var(--surface)",
  display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0", cursor: "pointer",
};
const stripLabel: React.CSSProperties = { writingMode: "vertical-rl", fontSize: 11.5, fontWeight: 700, color: "var(--faint)", letterSpacing: "0.08em", textTransform: "uppercase" };
const railTitle: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: "var(--text)" };
const clearBtn: React.CSSProperties = { border: "none", background: "transparent", color: "var(--accent)", fontSize: 11.5, fontWeight: 600, cursor: "pointer", padding: 0 };
const applyBtn: React.CSSProperties = {
  border: "1px solid var(--accent)", background: "var(--accent)", color: "#fff",
  borderRadius: 8, padding: "8px 12px", fontSize: 12.5, fontWeight: 700,
};
const iconBtn: React.CSSProperties = { border: "none", background: "transparent", color: "var(--body)", cursor: "pointer", padding: 3, display: "flex", alignItems: "center", borderRadius: 6 };
